import { Injectable } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { HttpService } from 'src/app/modules/core/services/http.service';
import { catchError, map } from 'rxjs/operators';
import { Dataset, DatasetType } from 'src/app/modules/core/store/models/dataset.model';
import { Document, FieldDataType, PicklistValue, QueryResult, SchemaField } from '../../store/models';
import { DatasetsRequest } from '../../store/models/dataset.model';

import { API_BASE_URL as apiBaseUrl } from '@app/core/constants/api-base-url';

@Injectable({ providedIn: 'root' })
export class PicklistService {
  constructor(private http: HttpService) {}

  getPicklistDatasets(): Observable<Dataset[]> {
    const datasetsRequest = {
      skip: 0,
      take: 1000,
      sortField: 'Name',
      sortDirection: 'Ascending',
      where: `type=${DatasetType.Picklist} AND IsActive==true`,
      includeWorkflow: false,
      skipChildren: true
    } as DatasetsRequest;

    const url = `${apiBaseUrl}/v1/meta/datasets/query`;

    return this.http.post(url, datasetsRequest).pipe(
      catchError(e => {
        console.error(e);
        return of(null);
      }),
      map(res => {
        return res?.data;
      })
    );
  }

  getPicklistDocuments(datasetUId: string): Observable<Document[]> {
    const queryString = this.http.queryStringFromObject({
      includeCount: false,
      forceWriteConnection: false
    });

    let params: any = {
      filterGroup: null,
      fields: null,
      sort: 'sort_order',
      pagination: {
        skip: 0,
        take: 1000
      }
    };

    const url = `${apiBaseUrl}/v1/data/datasets/${datasetUId}/query?${queryString}`;

    return this.http.post(url, params).pipe(
      catchError(e => {
        console.error(e);
        return of(null);
      }),
      map(res => {
        return res?.data;
      })
    );
  }

  getPicklistValues(field: SchemaField, forceApiCall = false): Observable<PicklistValue[]> {
    if (!field) {
      return of([]);
    }

    if (!field.picklistDatasetUId || !forceApiCall) {
      const result = Array.isArray(field.picklistValues) ? field.picklistValues : [];
      return of(result);
    }

    const url = `${apiBaseUrl}/v1/meta/datasets/field-picklist-values`;

    return this.http.post(url, field).pipe(
      catchError(e => {
        console.error(e);
        return of(null);
      }),
      map(res => {
        return res;
      })
    );
  }

  getDatasetPicklistValues(dataset: Dataset): Observable<any> {
    const pickListFields = dataset.schema?.fields.filter(f => f.dataType === FieldDataType.Picklist || f.dataType === FieldDataType.MultiPicklist);
    const datasetPicklists = new Map();

    const actions = [];

    if (pickListFields.length === 0) {
      return of(datasetPicklists);
    }

    pickListFields.forEach(f => {
      actions.push(this.getPicklistValues(f).pipe());
    });

    return forkJoin(actions).pipe(
      map((data) => {
        data.forEach((d, i) => {
          if (!datasetPicklists.has(pickListFields[i].systemName)) {
            datasetPicklists.set(pickListFields[i].systemName, d,);
          }
        });
        return datasetPicklists;
      })
    );
  }
}
